import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type { TestimonialsComponent as TestimonialsComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getFontFamily } from '../utils/fontLoader';

interface TestimonialsComponentProps {
  component: TestimonialsComponentType;
}

export const TestimonialsComponent: React.FC<TestimonialsComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Normalize data to handle alternative field names from Roary
  // Roary might send: testimonials[] with author instead of items[] with name
  const rawData = component.data as Record<string, unknown>;
  const testimonialItems = (rawData.items || rawData.testimonials || []) as Array<
    Record<string, unknown>
  >;

  // Normalize each testimonial item
  const normalizedItems = testimonialItems.map((item, index) => ({
    id: (item.id as string) || `testimonial-${index}`,
    name: (item.name as string) || (item.author as string) || 'Anonymous',
    role: item.role as string | undefined,
    company: item.company as string | undefined,
    quote: (item.quote as string) || (item.text as string) || '',
    rating: item.rating as number | undefined,
    avatarUrl:
      (item.avatarUrl as string) ||
      (item.imageUrl as string) ||
      (item.image as string) ||
      undefined,
    order: (item.order as number) ?? index,
  }));

  const data = {
    ...component.data,
    items: normalizedItems,
    layout: rawData.layout || 'grid',
    showRating: rawData.showRating !== false,
    showAvatar: rawData.showAvatar !== false,
    showCompany: rawData.showCompany !== false,
  } as typeof component.data;

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);

  // Color priority: component.customColors > theme colors
  const backgroundColor =
    data.customColors?.background || themeStyles.testimonials?.background || '#ffffff';
  const titleColor = data.customColors?.title || themeStyles.testimonials?.titleColor || '#111827';
  const subtitleColor =
    data.customColors?.subtitle || themeStyles.testimonials?.subtitleColor || '#6b7280';
  const cardBackground =
    data.customColors?.cardBackground || themeStyles.testimonials?.cardBackground || '#f9fafb';
  const cardBorder =
    data.customColors?.cardBorder || themeStyles.testimonials?.cardBorder || '#e5e7eb';
  const quoteColor =
    data.customColors?.quoteText || themeStyles.testimonials?.quoteColor || '#374151';
  const nameColor = data.customColors?.nameText || themeStyles.testimonials?.nameColor || '#111827';
  const roleColor = data.customColors?.roleText || themeStyles.testimonials?.roleColor || '#6b7280';
  const starColor =
    data.customColors?.starColor || themeStyles.testimonials?.starColor || '#fbbf24';

  const sortedItems = [...(data.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.testimonials?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.testimonials?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  // Determine if background is a gradient or solid color
  const isGradient = backgroundColor.includes('gradient');
  const backgroundStyle = isGradient ? { background: backgroundColor } : { backgroundColor };

  // Carousel navigation
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % sortedItems.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + sortedItems.length) % sortedItems.length);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (data.layout === 'carousel' && sortedItems.length > 1) {
      const timer = setInterval(nextSlide, 5000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.layout, sortedItems.length]);

  // Render star rating
  const renderRating = (rating: number = 5) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            size={16}
            fill={star <= rating ? starColor : 'none'}
            stroke={starColor}
            className="flex-shrink-0"
          />
        ))}
      </div>
    );
  };

  // Render avatar
  const renderAvatar = (item: (typeof sortedItems)[0]) => {
    if (!data.showAvatar) return null;

    if (item.avatarUrl) {
      return (
        <img src={item.avatarUrl} alt={item.name} className="w-12 h-12 rounded-full object-cover" />
      );
    }

    // Default avatar with initials
    const initials = item.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    return (
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
        style={{ backgroundColor: starColor }}
      >
        {initials}
      </div>
    );
  };

  // Render a single testimonial card
  const renderCard = (item: (typeof sortedItems)[0]) => (
    <div
      key={item.id}
      className="p-6 rounded-lg border h-full flex flex-col"
      style={{
        backgroundColor: cardBackground,
        borderColor: cardBorder,
      }}
    >
      {/* Quote icon */}
      <Quote size={32} className="mb-4 opacity-30" style={{ color: quoteColor }} />

      {/* Rating */}
      {data.showRating && item.rating && <div className="mb-4">{renderRating(item.rating)}</div>}

      {/* Quote text */}
      <div
        className="rich-text-content text-base leading-relaxed flex-grow mb-6"
        style={{ color: quoteColor, ...bodyFontStyle }}
        dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.quote) }}
      />

      {/* Author info */}
      <div className="flex items-center gap-3 mt-auto">
        {renderAvatar(item)}
        <div>
          <p className="font-semibold" style={{ color: nameColor, ...bodyFontStyle }}>
            {item.name}
          </p>
          {(item.role || item.company) && data.showCompany && (
            <p className="text-sm" style={{ color: roleColor, ...bodyFontStyle }}>
              {item.role}
              {item.role && item.company && ' at '}
              {item.company}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Grid layout
  if (data.layout === 'grid') {
    return (
      <section className="py-16 md:py-24" style={backgroundStyle}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: titleColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
            />
            {data.subtitle && (
              <div
                className="rich-text-content text-lg md:text-xl"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 [&>*]:min-w-0">
            {sortedItems.map(item => renderCard(item))}
          </div>

          {/* Call-to-Action Buttons */}
          {renderButtons()}
        </div>
      </section>
    );
  }

  // Carousel layout
  if (data.layout === 'carousel') {
    const currentItem = sortedItems[currentIndex];

    return (
      <section className="py-16 md:py-24" style={backgroundStyle}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: titleColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
            />
            {data.subtitle && (
              <div
                className="rich-text-content text-lg md:text-xl"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>

          {/* Carousel */}
          <div className="relative">
            {/* Navigation buttons */}
            {sortedItems.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors focus:outline-none"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft size={24} style={{ color: titleColor }} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors focus:outline-none"
                  aria-label="Next testimonial"
                >
                  <ChevronRight size={24} style={{ color: titleColor }} />
                </button>
              </>
            )}

            {/* Current testimonial */}
            {currentItem && (
              <div
                className="p-8 md:p-12 rounded-lg border text-center"
                style={{
                  backgroundColor: cardBackground,
                  borderColor: cardBorder,
                }}
              >
                {/* Quote icon */}
                <Quote
                  size={48}
                  className="mx-auto mb-6 opacity-30"
                  style={{ color: quoteColor }}
                />

                {/* Quote text */}
                <div
                  className="rich-text-content text-xl md:text-2xl leading-relaxed mb-8"
                  style={{ color: quoteColor, ...bodyFontStyle }}
                  dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(currentItem.quote) }}
                />

                {/* Rating */}
                {data.showRating && currentItem.rating && (
                  <div className="flex justify-center mb-6">{renderRating(currentItem.rating)}</div>
                )}

                {/* Author info */}
                <div className="flex flex-col items-center gap-3">
                  {renderAvatar(currentItem)}
                  <div>
                    <p
                      className="font-semibold text-lg"
                      style={{ color: nameColor, ...bodyFontStyle }}
                    >
                      {currentItem.name}
                    </p>
                    {(currentItem.role || currentItem.company) && data.showCompany && (
                      <p className="text-sm" style={{ color: roleColor, ...bodyFontStyle }}>
                        {currentItem.role}
                        {currentItem.role && currentItem.company && ' at '}
                        {currentItem.company}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Dots indicator */}
            {sortedItems.length > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {sortedItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors focus:outline-none ${
                      index === currentIndex ? 'bg-gray-800' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Call-to-Action Buttons */}
            {renderButtons()}
          </div>
        </div>
      </section>
    );
  }

  // List layout (default)
  return (
    <section className="py-16 md:py-24" style={backgroundStyle}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: titleColor, ...headingFontStyle }}
            dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
          />
          {data.subtitle && (
            <div
              className="rich-text-content text-lg md:text-xl"
              style={{ color: subtitleColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
            />
          )}
        </div>

        {/* List */}
        <div className="space-y-6">{sortedItems.map(item => renderCard(item))}</div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
